import { cn } from '@/lib/utils';
import type { InputHTMLAttributes, TextareaHTMLAttributes, SelectHTMLAttributes } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    label?: string;
}

export function Input({ label, className, id, ...props }: InputProps) {
    return (
        <div className="flex flex-col gap-1">
            {label && <label htmlFor={id} className="text-xs font-medium text-zinc-600">{label}</label>}
            <input
                id={id}
                className={cn(
                    'h-9 rounded-md border border-zinc-300 bg-white px-3 text-sm text-zinc-900',
                    'placeholder:text-zinc-400',
                    'focus:border-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-400',
                    'disabled:bg-zinc-50 disabled:text-zinc-400',
                    className,
                )}
                {...props}
            />
        </div>
    );
}

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    label?: string;
}

export function Textarea({ label, className, id, ...props }: TextareaProps) {
    return (
        <div className="flex flex-col gap-1">
            {label && <label htmlFor={id} className="text-xs font-medium text-zinc-600">{label}</label>}
            <textarea
                id={id}
                className={cn(
                    'min-h-[80px] rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900',
                    'placeholder:text-zinc-400 resize-y',
                    'focus:border-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-400',
                    className,
                )}
                {...props}
            />
        </div>
    );
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
    label?: string;
}

export function Select({ label, className, id, children, ...props }: SelectProps) {
    return (
        <div className="flex flex-col gap-1">
            {label && <label htmlFor={id} className="text-xs font-medium text-zinc-600">{label}</label>}
            <select
                id={id}
                className={cn(
                    'h-9 rounded-md border border-zinc-300 bg-white px-3 text-sm text-zinc-900',
                    'focus:border-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-400',
                    'disabled:bg-zinc-50 disabled:text-zinc-400',
                    className,
                )}
                {...props}
            >
                {children}
            </select>
        </div>
    );
}

interface SwitchProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label?: string;
}

export function Switch({ checked, onChange, label }: SwitchProps) {
    return (
        <label className="inline-flex cursor-pointer items-center gap-2">
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                onClick={() => onChange(!checked)}
                className={cn(
                    'relative h-5 w-9 rounded-full transition-colors',
                    checked ? 'bg-zinc-900' : 'bg-zinc-300',
                )}
            >
                <span
                    className={cn(
                        'absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white transition-transform',
                        checked && 'translate-x-4',
                    )}
                />
            </button>
            {label && <span className="text-sm text-zinc-700">{label}</span>}
        </label>
    );
}
